import {Suspense} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {Trans} from 'react-i18next';
import {diApi} from '@di-shared/api/di';
import {ExpectedError} from '@di-shared/features/common/CustomError';
import HalfDonutChart from '@di-shared/features/common/HalfDonutChart';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import environmentStore from '@di-shared/features/environment/environmentStore';
import useMileageSafeDriving, {
  SafeDrivingChallengeCondition,
  SafeDrivingType,
} from '@di-shared/features/mileage/safedriving/useMileageSafeDriving';
import {formatNumberWithCommas} from '@di-shared/utils';
import {getDDayString} from '@di-shared/utils/dDay';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import useUnitConverter from '@di-shared/utils/useUnitConverter';
import {parseDateWithFormat} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import {useSuspenseQuery} from '@tanstack/react-query';

interface MileageSafeDrivingProps {
  vin: string;
}

function getSafeDrivingGraphColor(safeDrivingType: SafeDrivingType) {
  switch (safeDrivingType) {
    case SafeDrivingType.GOOD:
      return 'var(--graph-gauge-active)';
    case SafeDrivingType.WARNING:
      return 'var(--tertiary)';
    case SafeDrivingType.BAD:
      return 'var(--quaternary-low)';
    default:
      return 'var(--surface-container-low)';
  }
}

function MileageSafeDriving({vin}: MileageSafeDrivingProps) {
  const {
    safeDrivingType,
    safetyDrvScore,
    safetyDrvScoreDiff,
    challengeDDay,
    rangeDrvDist,
    score95ConditionStatus,
    score85ConditionStatus,
    drivingDistanceConditionStatus,
    updatedDate,
  } = useMileageSafeDriving(vin);
  switch (safeDrivingType) {
    case SafeDrivingType.GOOD:
    case SafeDrivingType.WARNING:
    case SafeDrivingType.BAD:
      return (
        <MileageSafeDrivingChallenging
          challengeDDay={challengeDDay}
          safetyDrvScore={safetyDrvScore}
          safetyDrvScoreDiff={safetyDrvScoreDiff}
          rangeDrvDist={rangeDrvDist}
          safeDrivingType={safeDrivingType}
          score95ConditionStatus={score95ConditionStatus}
          score85ConditionStatus={score85ConditionStatus}
          drivingDistanceConditionStatus={drivingDistanceConditionStatus}
          updatedDate={updatedDate}
        />
      );
    case SafeDrivingType.IMPOSSIBLE_2024:
      return (
        <MileageSafeDrivingChallengeImpossible
          challengeDDay={challengeDDay}
          safetyDrvScore={safetyDrvScore}
          safetyDrvScoreDiff={safetyDrvScoreDiff}
          rangeDrvDist={rangeDrvDist}
          vin={vin}
        />
      );
    case SafeDrivingType.NO_DRIVING:
      return <MileageSafeDrivingNoRiding vin={vin} />;
    default:
      throw new ExpectedError(`Unknown SafeDriving type ${safeDrivingType}`);
  }
}

interface MileageSafeDrivingChallengingProps {
  challengeDDay: number;
  safetyDrvScore: number;
  safetyDrvScoreDiff: number;
  rangeDrvDist: number;
  safeDrivingType: SafeDrivingType;
  score95ConditionStatus: SafeDrivingChallengeCondition;
  score85ConditionStatus: SafeDrivingChallengeCondition;
  drivingDistanceConditionStatus: SafeDrivingChallengeCondition;
  updatedDate: Date | null;
}

export function MileageSafeDrivingChallenging({
  challengeDDay,
  safetyDrvScore,
  safetyDrvScoreDiff,
  rangeDrvDist,
  safeDrivingType,
  score95ConditionStatus,
  score85ConditionStatus,
  drivingDistanceConditionStatus,
  updatedDate,
}: MileageSafeDrivingChallengingProps) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();

  const {convertDistanceValue, convertDistanceSymbol} = useUnitConverter();
  return (
    <>
      <div className="section_heading type_main">
        <h3
          className="section_heading_title"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T30258')}
          <span className="badge badge_basic_container badge_25px">{getDDayString(challengeDDay)}</span>
        </h3>
        <p className="section_heading_desc">{t('T30259')}</p>
      </div>
      <div className="driving_mileage_graph_area">
        <HalfDonutChart
          color={getSafeDrivingGraphColor(safeDrivingType)}
          ratio={safetyDrvScore / 100}
          width={220}
          strokeWidth={8}
        />
        <div className="driving_mileage_graph_info">
          <span className="driving_mileage_graph_score">
            <Trans
              i18nKey="T30261_$score"
              values={{score: safetyDrvScore}}
              components={{
                Value: <span className="score_value" />,
                Unit: <span className="score_unit" />,
              }}
            />
          </span>
          <span className="driving_mileage_graph_compare">
            <span className="compare_text">{t('T30174')}</span>
            <span className="compare_value">
              {safetyDrvScoreDiff >= 0 ? '+' : '-'}
              {t('T20006_$score', {score: Math.abs(safetyDrvScoreDiff)})}
            </span>
          </span>
        </div>
      </div>
      <div className="driving_mileage_graph_distance">
        <span className="distance_text">{t('T30260')}</span>
        <span className="distance_value">
          {formatNumberWithCommas(convertDistanceValue(rangeDrvDist))}
          {convertDistanceSymbol(rangeDrvDist)}
        </span>
      </div>
      <div className="section_container">
        <div className="section_container_header">
          <h4 className="title_xsmall_b">{t('T30262')}</h4>
        </div>
        <p
          className="section_container_desc"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T30263')}
        </p>

        <span className="section_container_caption">
          {t('T30001')} : {updatedDate && CldrDatetimeFormatter.format(updatedDate, 'yMMMd')}
        </span>

        <div className="mileage_info type_driving_mileage">
          <ul className="mileage_info_list">
            <li className="mileage_info_item">
              <div className="mileage_info_key">{t('T30264_$score', {score: 95})}</div>
              <div className="mileage_info_value">
                {t('T30265_$mileage', {mileage: '5,000'})}
                <div className="badge_box">
                  {score95ConditionStatus === SafeDrivingChallengeCondition.MET ? (
                    <span className="badge badge_basic_container badge_20px">{t('T20046')}</span>
                  ) : score95ConditionStatus === SafeDrivingChallengeCondition.NOT_MET ? (
                    <span className="badge badge_container_red badge_20px">{t('T20047')}</span>
                  ) : (
                    ''
                  )}
                </div>
              </div>
            </li>
            <li className="mileage_info_item has_badge">
              <div className="mileage_info_key">{t('T30264_$score', {score: 85})}</div>
              <div className="mileage_info_value">
                {t('T30265_$mileage', {mileage: '3,000'})}
                <div className="badge_box">
                  {score85ConditionStatus === SafeDrivingChallengeCondition.MET ? (
                    <span className="badge badge_basic_container badge_20px">{t('T20046')}</span>
                  ) : score85ConditionStatus === SafeDrivingChallengeCondition.NOT_MET ? (
                    <span className="badge badge_container_red badge_20px">{t('T20047')}</span>
                  ) : (
                    ''
                  )}
                </div>
              </div>
            </li>
            <li className="mileage_info_item type_total has_badge">
              <div className="mileage_info_key">{t('T10141')}</div>
              <div className="mileage_info_value">
                {t('T30266_$distance', {distance: convertDistanceValue(500) + convertDistanceSymbol(500)})}
                <div className="badge_box">
                  {drivingDistanceConditionStatus === SafeDrivingChallengeCondition.MET ? (
                    <span className="badge badge_basic_container badge_20px">{t('T20046')}</span>
                  ) : drivingDistanceConditionStatus === SafeDrivingChallengeCondition.NOT_MET ? (
                    <span className="badge badge_container_red badge_20px">{t('T20047')}</span>
                  ) : (
                    ''
                  )}
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </>
  );
}

export default MileageSafeDriving;

export function MileageSafeDrivingNoRiding({vin}: MileageSafeDrivingErrorProps) {
  const {t} = useInferableTranslation();
  const {convertDistanceSymbol, convertDistanceValue, defaultDistanceSymbol} = useUnitConverter();
  return (
    <>
      <div className="section_heading type_main">
        <h3 className="section_heading_title">{t('T30258')}</h3>
        <p className="section_heading_desc">{t('T30259')}</p>
      </div>
      <div className="driving_mileage_graph_area">
        <HalfDonutChart
          color={getSafeDrivingGraphColor(SafeDrivingType.NO_DRIVING)}
          ratio={1}
          width={220}
          strokeWidth={8}
        />
        <div className="driving_mileage_graph_info">
          <span className="driving_mileage_graph_score">
            <Trans
              i18nKey="T30261_$score"
              values={{score: 0}}
              components={{
                Value: <span className="score_value" />,
                Unit: <span className="score_unit" />,
              }}
            />
          </span>
          <span className="driving_mileage_graph_compare">
            <span className="compare_text">{t('T30174')}</span>
            <span className="compare_value">+{t('T20006_$score', {score: 0})}</span>
          </span>
        </div>
      </div>
      <div className="driving_mileage_graph_distance">
        <span className="distance_text">{t('T30260')}</span>
        <span className="distance_value">0{defaultDistanceSymbol}</span>
      </div>
      <div className="section_container">
        <div className="section_container_header">
          <h4 className="title_xsmall_b">{t('T30262')}</h4>
        </div>
        <p
          className="section_container_desc"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T30263')}
        </p>
        <ErrorBoundary fallback={<></>}>
          <Suspense fallback={<MileageSafeDrivingUpdatedDateLoading />}>
            <MileageSafeDrivingUpdatedDate vin={vin} />
          </Suspense>
        </ErrorBoundary>

        <div className="mileage_info type_driving_mileage">
          <ul className="mileage_info_list">
            <li className="mileage_info_item">
              <div className="mileage_info_key">{t('T30264_$score', {score: 95})}</div>
              <div className="mileage_info_value">{t('T30265_$mileage', {mileage: '5,000'})}</div>
            </li>
            <li className="mileage_info_item has_badge">
              <div className="mileage_info_key">{t('T30264_$score', {score: 85})}</div>
              <div className="mileage_info_value">{t('T30265_$mileage', {mileage: '3,000'})}</div>
            </li>
            <li className="mileage_info_item type_total has_badge">
              <div className="mileage_info_key">{t('T10141')}</div>
              <div className="mileage_info_value">
                {t('T30266_$distance', {distance: convertDistanceValue(500) + convertDistanceSymbol(500)})}
              </div>
            </li>
          </ul>
        </div>
      </div>
    </>
  );
}

interface MileageSafeDrivingErrorProps {
  vin: string;
}

export function MileageSafeDrivingError({vin}: MileageSafeDrivingErrorProps) {
  const {t} = useInferableTranslation();
  const {convertDistanceSymbol, convertDistanceValue} = useUnitConverter();
  return (
    <>
      <div className="section_heading type_main">
        <h3 className="section_heading_title">{t('T30258')}</h3>
        <p className="section_heading_desc">{t('T30259')}</p>
      </div>
      <div className="driving_mileage_graph_area">
        <HalfDonutChart
          color={getSafeDrivingGraphColor(SafeDrivingType.ETC)}
          ratio={1}
          width={220}
          strokeWidth={8}
        />
        <div className="driving_mileage_graph_info">
          <span className="driving_mileage_graph_score">
            <span className="score_value">--</span>
            <span className="score_unit">점</span>
          </span>
          <span className="driving_mileage_graph_compare">
            <span className="compare_text">{t('T30174')}</span>
            <span className="compare_value">{t('T20006_$score', {score: '-'})}</span>
          </span>
        </div>
      </div>
      <div className="driving_mileage_graph_distance">
        <span className="distance_text">{t('T30260')}</span>
        <span className="distance_value">0{convertDistanceSymbol(0)}</span>
      </div>
      <div className="section_container">
        <div className="section_container_header">
          <h4 className="title_xsmall_b">{t('T30262')}</h4>
        </div>
        <p
          className="section_container_desc"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T30263')}
        </p>
        <ErrorBoundary fallback={<></>}>
          <Suspense fallback={<MileageSafeDrivingUpdatedDateLoading />}>
            <MileageSafeDrivingUpdatedDate vin={vin} />
          </Suspense>
        </ErrorBoundary>

        <div className="mileage_info type_driving_mileage">
          <ul className="mileage_info_list">
            <li className="mileage_info_item">
              <div className="mileage_info_key">{t('T30264_$score', {score: 95})}</div>
              <div className="mileage_info_value">{t('T30265_$mileage', {mileage: '5,000'})}</div>
            </li>
            <li className="mileage_info_item has_badge">
              <div className="mileage_info_key">{t('T30264_$score', {score: 85})}</div>
              <div className="mileage_info_value">{t('T30265_$mileage', {mileage: '3,000'})}</div>
            </li>
            <li className="mileage_info_item type_total has_badge">
              <div className="mileage_info_key">{t('T10141')}</div>
              <div className="mileage_info_value">
                {t('T30266_$distance', {distance: convertDistanceValue(500) + convertDistanceSymbol(500)})}
              </div>
            </li>
          </ul>
        </div>
      </div>
    </>
  );
}

interface MileageSafeDrivingChallengeImpossibleProps {
  challengeDDay: number;
  safetyDrvScore: number;
  safetyDrvScoreDiff: number;
  rangeDrvDist: number;
  vin: string;
}

export function MileageSafeDrivingChallengeImpossible({
  challengeDDay,
  safetyDrvScore,
  safetyDrvScoreDiff,
  rangeDrvDist,
  vin,
}: MileageSafeDrivingChallengeImpossibleProps) {
  const {t} = useInferableTranslation();
  const {convertDistanceValue, convertDistanceSymbol} = useUnitConverter();
  return (
    <>
      <div className="section_heading type_main">
        <h3
          className="section_heading_title"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T30285')}
          <span className="badge badge_basic_container badge_25px">{getDDayString(challengeDDay)}</span>
        </h3>
        <p className="section_heading_desc">{t('T30259')}</p>
      </div>
      <div className="driving_mileage_graph_area type_fail">
        <HalfDonutChart
          color={getSafeDrivingGraphColor(SafeDrivingType.IMPOSSIBLE_2024)}
          ratio={safetyDrvScore / 100}
          width={220}
          strokeWidth={8}
        />
        <div className="driving_mileage_graph_info">
          <span className="driving_mileage_graph_score">
            <Trans
              i18nKey="T30261_$score"
              values={{score: safetyDrvScore}}
              components={{
                Value: <span className="score_value" />,
                Unit: <span className="score_unit" />,
              }}
            />
          </span>
          <span className="driving_mileage_graph_compare">
            <span className="compare_text">{t('T30174')}</span>
            <span className="compare_value">
              {safetyDrvScoreDiff >= 0 ? '+' : '-'}
              {t('T20006_$score', {score: Math.abs(safetyDrvScoreDiff)})}
            </span>
          </span>
        </div>
      </div>
      <div className="driving_mileage_graph_distance">
        <span className="distance_text">{t('T30260')}</span>
        <span className="distance_value">
          {formatNumberWithCommas(convertDistanceValue(rangeDrvDist))}
          {convertDistanceSymbol(rangeDrvDist)}
        </span>
      </div>
      <div className="section_container">
        <div className="section_container_header">
          <h4 className="title_xsmall_b">{t('T30262')}</h4>
        </div>
        <p
          className="section_container_desc"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T30263')}
        </p>
        <ErrorBoundary fallback={<></>}>
          <Suspense fallback={<MileageSafeDrivingUpdatedDateLoading />}>
            <ErrorBoundary fallback={<></>}>
              <Suspense fallback={<MileageSafeDrivingUpdatedDateLoading />}>
                <MileageSafeDrivingUpdatedDate vin={vin} />
              </Suspense>
            </ErrorBoundary>
          </Suspense>
        </ErrorBoundary>

        <div className="mileage_info type_driving_mileage">
          <ul className="mileage_info_list">
            <li className="mileage_info_item">
              <div className="mileage_info_key">{t('T30264_$score', {score: 95})}</div>
              <div className="mileage_info_value">{t('T30265_$mileage', {mileage: '5,000'})}</div>
            </li>
            <li className="mileage_info_item has_badge">
              <div className="mileage_info_key">{t('T30264_$score', {score: 85})}</div>
              <div className="mileage_info_value">{t('T30265_$mileage', {mileage: '3,000'})}</div>
            </li>
            <li className="mileage_info_item type_total has_badge">
              <div className="mileage_info_key">{t('T10141')}</div>
              <div className="mileage_info_value">{t('T30266_$distance', {distance: '500km'})}</div>
            </li>
          </ul>
        </div>
      </div>
    </>
  );
}

export function MileageSafeDrivingLoading() {
  const {t} = useInferableTranslation();
  const {defaultDistanceSymbol} = useUnitConverter();
  return (
    <>
      <div className="section_heading type_main">
        <h3 className="section_heading_title skeleton">
          {t('T30267')}
          <span className="badge badge_basic_container badge_25px skeleton">D-</span>
        </h3>
        <p className="section_heading_desc skeleton">{t('T30259')}</p>
      </div>
      <div className="driving_mileage_graph_area type_fail skeleton">
        <div className="driving_mileage_graph_info">
          <span className="driving_mileage_graph_score">
            <Trans
              i18nKey="T30261_$score"
              values={{score: 0}}
              components={{
                Value: <span className="score_value" />,
                Unit: <span className="score_unit" />,
              }}
            />
          </span>
          <span className="driving_mileage_graph_compare">
            <span className="compare_text">{t('T30174')}</span>
            <span className="compare_value">{t('T20006_$score', {score: 0})}</span>
          </span>
        </div>
      </div>
      <div className="driving_mileage_graph_distance skeleton">
        <span className="distance_text">{t('T30260')}</span>
        <span className="distance_value">{defaultDistanceSymbol}</span>
      </div>
      <div className="section_container">
        <div className="section_container_header skeleton">
          <h4 className="title_xsmall_b">{t('T30262')}</h4>
        </div>
        <p className="section_container_desc skeleton">{t('T30263')}</p>
        <span className="section_container_caption skeleton">{t('T30001')}</span>

        <div className="mileage_info type_driving_mileage">
          <ul className="mileage_info_list">
            <li className="mileage_info_item skeleton">
              <div className="mileage_info_key">{t('T30264_$score', {score: 95})}</div>
              <div className="mileage_info_value">{t('T30265_$mileage', {mileage: '5,000'})}</div>
            </li>
            <li className="mileage_info_item has_badge skeleton">
              <div className="mileage_info_key">{t('T30264_$score', {score: 85})}</div>
              <div className="mileage_info_value">{t('T30265_$mileage', {mileage: '3,000'})}</div>
            </li>
            <li className="mileage_info_item type_total has_badge skeleton">
              <div className="mileage_info_key">{t('T10141')}</div>
              <div className="mileage_info_value">{t('T30266_$distance', {distance: '500km'})}</div>
            </li>
          </ul>
        </div>
      </div>
    </>
  );
}

function MileageSafeDrivingUpdatedDate({vin}: {vin: string}) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const ccid = environmentStore.getState().ccid;
  const {data: updatedAt} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.maintenancesSestController.getLastOdometer1), vin, ccid],
    queryFn: () => diApi.maintenancesSestController.getLastOdometer1({vin, ccid}),
    select: (data) => parseDateWithFormat(data.data.updatedAt),
  });
  return (
    <span className="section_container_caption">
      {t('T30001')} : {updatedAt && CldrDatetimeFormatter.format(updatedAt, 'yMMMd')}
    </span>
  );
}

function MileageSafeDrivingUpdatedDateLoading() {
  const {t} = useInferableTranslation();
  return <span className="section_container_caption skeleton">{t('T30001')} :</span>;
}
